import React, { useState, useEffect, useMemo } from "react";
import { flattenObject } from "./utils/translationUtils";
import { TranslationObject, TranslationFilters, SearchResults } from "./types";

interface TranslationEditorProps {
  translation: TranslationObject;
  masterTranslation: TranslationObject;
  lang: string;
  masterLang: string;
  onUpdate: (lang: string, key: string, value: string) => Promise<any>;
  onCopyFromMaster: (lang: string, key: string) => Promise<any>;
  onTranslateWithAI: (lang: string, key: string | string[]) => Promise<any>;
  onDeleteKeys: (lang: string, keys: string[]) => Promise<any>;
  searchResults?: SearchResults;
  filters: TranslationFilters;
}

type FlattenedTranslation = Record<string, string>;

const TranslationEditor: React.FC<TranslationEditorProps> = ({
  translation,
  masterTranslation,
  lang,
  masterLang,
  onUpdate,
  onCopyFromMaster,
  onTranslateWithAI,
  onDeleteKeys,
  searchResults = null,
  filters = {} as TranslationFilters,
}) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [flattenedTranslation, setFlattenedTranslation] =
    useState<FlattenedTranslation>({});
  const [flattenedMaster, setFlattenedMaster] = useState<FlattenedTranslation>(
    {}
  );
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());
  const [translatingKeys, setTranslatingKeys] = useState<Set<string>>(
    new Set()
  );
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (translation) {
      setFlattenedTranslation(flattenObject(translation));
    }
  }, [translation]);

  useEffect(() => {
    if (masterTranslation) {
      setFlattenedMaster(flattenObject(masterTranslation));
    }
  }, [masterTranslation]);

  const handleEdit = (key: string) => {
    setEditingKey(key);
    setEditValue(flattenedTranslation[key] || "");
  };

  const handleSave = async () => {
    if (editingKey) {
      setUpdatingKeys((prev) => new Set([...prev, editingKey]));

      try {
        await onUpdate(lang, editingKey, editValue);
      } finally {
        setUpdatingKeys((prev) => {
          const newSet = new Set([...prev]);
          newSet.delete(editingKey);
          return newSet;
        });
        setEditingKey(null);
      }
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
  };

  const handleCopyFromMaster = async (key: string) => {
    setUpdatingKeys((prev) => new Set([...prev, key]));

    try {
      await onCopyFromMaster(lang, key);
    } finally {
      setUpdatingKeys((prev) => {
        const newSet = new Set([...prev]);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleTranslateWithAI = async (key: string) => {
    setTranslatingKeys((prev) => new Set([...prev, key]));

    try {
      await onTranslateWithAI(lang, key);
    } finally {
      setTranslatingKeys((prev) => {
        const newSet = new Set([...prev]);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  // Toggle key selection
  const toggleKeySelection = (key: string) => {
    setSelectedKeys((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(key)) {
        newSelection.delete(key);
      } else {
        newSelection.add(key);
      }
      return newSelection;
    });
  };

  // Select or deselect all currently visible keys
  const toggleSelectAll = () => {
    if (selectedKeys.size === displayKeys.length) {
      // Deselect all if all are selected
      setSelectedKeys(new Set());
    } else {
      // Select all visible keys
      setSelectedKeys(new Set(displayKeys));
    }
  };

  // Handle bulk delete of selected keys
  const handleDeleteSelected = async () => {
    if (selectedKeys.size === 0) return;

    const keysToDelete = [...selectedKeys];
    setUpdatingKeys((prev) => new Set([...prev, ...keysToDelete]));

    try {
      await onDeleteKeys(lang, keysToDelete);
      setSelectedKeys(new Set()); // Clear selection after deletion
    } finally {
      setUpdatingKeys((prev) => {
        const newSet = new Set([...prev]);
        keysToDelete.forEach((key) => newSet.delete(key));
        return newSet;
      });
    }
  };

  // Handle bulk translate of selected keys
  const handleTranslateSelected = async () => {
    if (selectedKeys.size === 0) return;

    const keysToTranslate = [...selectedKeys];
    setTranslatingKeys((prev) => new Set([...prev, ...keysToTranslate]));

    try {
      // Instead of processing keys sequentially, use the bulk translation method
      await onTranslateWithAI(lang, keysToTranslate);
    } finally {
      setTranslatingKeys((prev) => {
        const newSet = new Set([...prev]);
        keysToTranslate.forEach((key) => newSet.delete(key));
        return newSet;
      });
    }
  };

  // Handle bulk copy from master for selected keys
  const handleCopySelectedFromMaster = async () => {
    if (selectedKeys.size === 0) return;

    const keysToCopy = [...selectedKeys];
    setUpdatingKeys((prev) => new Set([...prev, ...keysToCopy]));

    try {
      // Process keys sequentially to avoid overwhelming the API
      for (const key of keysToCopy) {
        await onCopyFromMaster(lang, key);
      }
    } finally {
      setUpdatingKeys((prev) => {
        const newSet = new Set([...prev]);
        keysToCopy.forEach((key) => newSet.delete(key));
        return newSet;
      });
    }
  };

  // Apply filters to keys
  const displayKeys = useMemo(() => {
    // Start with search results or all keys
    let keys = searchResults || Object.keys(flattenedTranslation);

    // Apply filters if any are active
    if (
      filters.showMissing ||
      filters.showEmpty ||
      filters.showIdentical ||
      filters.showNonMaster
    ) {
      keys = keys.filter((key) => {
        const value = flattenedTranslation[key] || "";
        const masterValue = flattenedMaster[key] || "";
        const isKeyInMaster = key in flattenedMaster;

        if (filters.showMissing && !value && masterValue) {
          return true;
        }

        if (filters.showEmpty && value === "") {
          return true;
        }

        if (
          filters.showIdentical &&
          value &&
          masterValue &&
          value === masterValue &&
          masterLang !== lang
        ) {
          return true;
        }

        if (filters.showNonMaster && !isKeyInMaster) {
          return true;
        }

        // If none of the active filters match, don't include this key
        if (
          filters.showMissing ||
          filters.showEmpty ||
          filters.showIdentical ||
          filters.showNonMaster
        ) {
          return false;
        }

        return true;
      });
    }

    return keys;
  }, [
    searchResults,
    flattenedTranslation,
    flattenedMaster,
    filters,
    masterLang,
    lang,
  ]);

  // Calculate if all displayKeys are selected
  const allSelected =
    displayKeys.length > 0 && selectedKeys.size === displayKeys.length;
  const someSelected = selectedKeys.size > 0;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Selection action panel - shows when items are selected */}
      {someSelected && (
        <div className="bg-cyan-50 border-b border-cyan-200 p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-cyan-700">
              {selectedKeys.size} {selectedKeys.size === 1 ? "item" : "items"}{" "}
              selected
            </div>
            <div className="flex space-x-2">
              {masterLang !== lang && (
                <>
                  <button
                    onClick={handleCopySelectedFromMaster}
                    className="px-2 py-1 text-xs bg-white border border-cyan-300 rounded text-cyan-700 hover:bg-cyan-100"
                  >
                    Copy from {masterLang}
                  </button>
                  <button
                    onClick={handleTranslateSelected}
                    className="px-2 py-1 text-xs bg-cyan-600 border border-cyan-700 rounded text-white hover:bg-cyan-700"
                  >
                    Translate with AI
                  </button>
                </>
              )}
              <button
                onClick={handleDeleteSelected}
                className="px-2 py-1 text-xs bg-red-100 border border-red-300 rounded text-red-700 hover:bg-red-200"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedKeys(new Set())}
                className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table header with select all checkbox */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            {allSelected ? "Deselect all" : "Select all"}
          </span>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {displayKeys.map((key) => {
          const value = flattenedTranslation[key] || "";
          const masterValue = flattenedMaster[key] || "";
          const isMissing = !value && masterValue;
          const isEmpty = value === "";
          const isIdentical =
            value &&
            masterValue &&
            value === masterValue &&
            masterLang !== lang;
          const isNonMaster = !(key in flattenedMaster);
          const isEditing = editingKey === key;
          const isUpdating = updatingKeys.has(key);
          const isTranslating = translatingKeys.has(key);
          const isSelected = selectedKeys.has(key);

          return (
            <div
              key={key}
              className={`p-4 hover:bg-gray-50 ${
                isSelected
                  ? "bg-cyan-50 border-l-4 border-cyan-500"
                  : isNonMaster
                  ? "bg-purple-50"
                  : isMissing
                  ? "bg-amber-50"
                  : isEmpty
                  ? "bg-amber-50"
                  : isIdentical
                  ? "bg-blue-50"
                  : ""
              }`}
            >
              <div className="flex items-start">
                <div className="mr-3 pt-1">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleKeySelection(key)}
                    className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 break-all">
                    {key}
                  </p>

                  {isEditing ? (
                    <div className="mt-2">
                      <textarea
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className="mt-2 flex space-x-2">
                        <button
                          onClick={handleSave}
                          disabled={isUpdating}
                          className={`px-3 py-1 rounded-md text-sm ${
                            isUpdating
                              ? "bg-cyan-400 text-white cursor-not-allowed"
                              : "bg-cyan-600 text-white hover:bg-cyan-700"
                          }`}
                        >
                          {isUpdating ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={isUpdating}
                          className="px-3 py-1 bg-gray-200 text-gray-800 rounded-md text-sm hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <div className="flex items-center mt-1">
                          <p
                            className={`text-sm ${
                              !value
                                ? "text-amber-600 italic"
                                : isIdentical
                                ? "text-blue-600"
                                : "text-gray-700"
                            }`}
                          >
                            {value || "Empty"}
                          </p>
                          {isIdentical && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              Identical to master
                            </span>
                          )}
                          {isNonMaster && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                              Not in master
                            </span>
                          )}
                        </div>

                        {/* Show loading overlay during updates */}
                        {(isUpdating || isTranslating) && (
                          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                            <div className="text-xs text-cyan-600 font-medium">
                              {isUpdating ? "Updating..." : "Translating..."}
                            </div>
                          </div>
                        )}
                      </div>

                      {masterValue && masterLang !== lang && (
                        <div className="mt-1 text-xs text-gray-500 border-l-2 border-gray-200 pl-2">
                          <span className="font-medium">{masterLang}:</span>{" "}
                          {masterValue}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {!isEditing && (
                  <div className="ml-4 flex-shrink-0 flex space-x-1">
                    <button
                      onClick={() => handleEdit(key)}
                      disabled={isUpdating || isTranslating}
                      className={`text-xs bg-white border border-gray-300 rounded px-2 py-1 ${
                        isUpdating || isTranslating
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      Edit
                    </button>

                    {masterLang !== lang && (
                      <>
                        <button
                          onClick={() => handleCopyFromMaster(key)}
                          disabled={isUpdating || isTranslating}
                          className={`text-xs bg-white border border-gray-300 rounded px-2 py-1 ${
                            isUpdating || isTranslating
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-gray-100"
                          }`}
                          title={`Copy from ${masterLang}`}
                        >
                          Copy
                        </button>

                        <button
                          onClick={() => handleTranslateWithAI(key)}
                          disabled={isUpdating || isTranslating}
                          className={`text-xs border rounded px-2 py-1 ${
                            isUpdating || isTranslating
                              ? "bg-cyan-50 border-cyan-200 text-cyan-400 opacity-50 cursor-not-allowed"
                              : "bg-cyan-100 border-cyan-300 text-cyan-700 hover:bg-cyan-200"
                          }`}
                          title="Translate with AI"
                        >
                          AI
                        </button>
                      </>
                    )}

                    <button
                      onClick={async () => {
                        // Directly delete the single key without affecting selection state
                        setUpdatingKeys((prev) => new Set([...prev, key]));
                        try {
                          await onDeleteKeys(lang, [key]);
                        } finally {
                          setUpdatingKeys((prev) => {
                            const newSet = new Set([...prev]);
                            newSet.delete(key);
                            return newSet;
                          });
                        }
                      }}
                      disabled={isUpdating || isTranslating}
                      className={`text-xs border rounded px-2 py-1 ${
                        isUpdating || isTranslating
                          ? "bg-red-50 border-red-200 text-red-400 opacity-50 cursor-not-allowed"
                          : "bg-red-50 border-red-300 text-red-600 hover:bg-red-100"
                      }`}
                      title="Delete key"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {displayKeys.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No translations found
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationEditor;
